import { ChangeDetectorRef, Component, NgZone, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AuthAnimationService, AuthAnimationState } from '../../services/auth-animation.service';
import { AuthStateService } from 'src/app/services/auth-state.service';

type CssAnimationEvent = globalThis.AnimationEvent;

@Component({
  selector: 'app-auth-brand-intro',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './auth-brand-intro.component.html',
  styleUrls: ['./auth-brand-intro.component.scss'],
})
export class AuthBrandIntroComponent implements OnDestroy {
  private readonly authAnimationService = inject(AuthAnimationService);
  private readonly authStateService = inject(AuthStateService);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);

  private static readonly GREETING_ENTER_DELAY_MS = 60;
  private static readonly GREETING_EXIT_DELAY_MS = 3600;
  private static readonly GREETING_EXIT_DURATION_MS = 600;
  private static readonly GREETING_FALLBACK_DELAY_MS =
    AuthBrandIntroComponent.GREETING_ENTER_DELAY_MS +
    AuthBrandIntroComponent.GREETING_EXIT_DELAY_MS +
    AuthBrandIntroComponent.GREETING_EXIT_DURATION_MS;
  private static readonly LOGO_DROP_DURATION_MS = 900;

  readonly state$ = this.authAnimationService.state$.pipe(
    tap((state) => this.handleStateSideEffects(state)),
  );

  readonly userFirstName$: Observable<string> = this.authStateService.user$.pipe(
    map((user) => (user?.firstName ?? '').trim()),
  );

  showIntroTagline = true;
  showGreeting = false;
  logoShouldDrop = false;
  logoShouldScaleUp = false;
  overlayShouldFade = false;
  /** Removes the overlay from the DOM once its white fade-out has finished. */
  overlayDismissed = false;

  private logoDropTriggered = false;
  private greetingDropTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastInitializedPlayId: number | null = null;


  ngOnDestroy(): void {
    this.clearGreetingDropTimeout();
  }

  handleIntroTaglineAnimationEnd(): void {
    if (!this.showGreeting) {
      this.showIntroTagline = false;
      this.showGreeting = true;
      this.scheduleLogoDropFallback();
    }
  }

  handleGreetingAnimationEnd(event: CssAnimationEvent): void {
    const animationName = event.animationName?.toLowerCase() ?? '';

    if (animationName.includes('taglineexit')) {
      this.showGreeting = false;
      this.triggerLogoDrop();
    }
  }

  private scheduleLogoDropFallback(): void {
    if (this.logoDropTriggered || this.logoShouldDrop) {
      return;
    }

    this.clearGreetingDropTimeout();
    this.greetingDropTimeoutId = setTimeout(() => {
      this.triggerLogoDrop();
    }, AuthBrandIntroComponent.GREETING_FALLBACK_DELAY_MS);
  }

  private triggerLogoDrop(): void {
    if (this.logoDropTriggered) {
      return;
    }

    this.logoDropTriggered = true;
    this.ngZone.run(() => {
      this.logoShouldDrop = true;
      this.cdr.markForCheck();
    });

    this.clearGreetingDropTimeout();
  }

  private clearGreetingDropTimeout(): void {
    if (this.greetingDropTimeoutId !== null) {
      clearTimeout(this.greetingDropTimeoutId);
      this.greetingDropTimeoutId = null;
    }
  }

  handleLogoDropAnimationEnd(event: CssAnimationEvent): void {
    const animationName = event.animationName?.toLowerCase() ?? '';
    if (!animationName.includes('logodrop') || this.logoShouldScaleUp) {
      return;
    }

    this.logoShouldScaleUp = true;
    this.overlayShouldFade = true;
    // Hand-off: kick the dashboard reveal the moment the white fade begins so
    // the dashboard fades in from white underneath this overlay — instead of
    // waiting for the fixed brand-fade timer (which left a gap + re-reveal).
    this.authAnimationService.emitDashboardRevealIfQueued();
    this.cdr.markForCheck();
  }

  handleOverlayFadeOutEnd(event: CssAnimationEvent): void {
    const animationName = event.animationName?.toLowerCase() ?? '';
    if (!animationName.includes('brandtodashboardwhite')) {
      return;
    }
    // The white hand-off has finished — remove the (now transparent) overlay so
    // it doesn't sit over the dashboard blocking interaction until the timer.
    this.overlayDismissed = true;
    this.cdr.markForCheck();
  }

  private handleStateSideEffects(state: AuthAnimationState): void {
    if (state.active && state.type === 'brandFadeIn') {
      if (this.lastInitializedPlayId !== state.playId) {
        this.lastInitializedPlayId = state.playId;
        this.resetSequence();
      }
      return;
    }

    if (!state.active && this.lastInitializedPlayId !== null) {
      this.lastInitializedPlayId = null;
      this.resetSequence();
    }
  }

  private resetSequence(): void {
    this.showIntroTagline = true;
    this.showGreeting = false;
    this.logoShouldDrop = false;
    this.logoShouldScaleUp = false;
    this.overlayShouldFade = false;
    this.overlayDismissed = false;
    this.logoDropTriggered = false;
    this.clearGreetingDropTimeout();
  }
}
